import { useState, useMemo } from "react";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import styled from "styled-components";
import {
  Input,
  IndeterminateProgressCircle,
  LOADER_SIZE,
  ListItem,
  ListItemCheckbox,
  ListItemText,
  ListItemPrimaryText,
  ListItemSubtext,
  Heading,
  HEADING_TYPES,
  Card,
  BUTTON_VARIANTS,
  Chip,
  Dialog,
  SimpleTable,
  SimpleTableBody,
  SimpleTableWrapper,
  SimpleTableTitle,
  SimpleTableContainer,
  SimpleTableToolbar,
  SimpleTableRow,
  SimpleTableData,
  TOAST_VARIANTS,
} from "cdk-radial";
import useDebounce from "../../hooks/debounce";
import RoleRow from "../../UserProfileManager/RoleRow";

const TallListItem = styled(ListItem)`
  height: 3.5rem;
`;

const UserNameText = styled(Heading)`
  color: #6b2574;
`;

const SearchBarContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const SearchInput = styled(Input)`
  flex: 0 0 100%;
`;

const UserListCard = styled(Card)`
  max-height: 200px;
  overflow-y: auto;
  display: block;
`;

const CopyDialog = styled(Dialog)`
  width: 600px;

  & > :nth-child(2) {
    border-bottom: 1px solid rgba(21, 27, 37, 0.3);
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }
`;

const ReviewDialog = styled(Dialog)`
  width: 700px;
`;

const ReviewDialogContainer = styled(SimpleTableContainer)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UserChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0.75rem 0rem;
`;

const UserTableData = styled(SimpleTableData)`
  display: flex;
  flex-direction: column;
`;

const RolesTableTitle = styled(SimpleTableTitle)`
  padding: 0.5rem 1rem 0px 0px;
  fontsize: 18px;
`;

const RoleTableData = styled(SimpleTableData)`
  padding: 0;
  border-left: 1px solid #c4c5c8;
`;

const RolesToolbar = styled(SimpleTableToolbar)`
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: rgba(21, 27, 37, 0.9) 0px -2px 0px inset;
`;

function CopyRolesDialog({
  fetchResults,
  userRoles,
  fetchUserRoles,
  debounceDelay = 300,
  id = "user-typeahead-search",
  name = "user-typeahead-search",
  userProfile,
  isCopyDialogOpen,
  setIsCopyDialogOpen,
  getDifferencesOfRoleLists,
  assignRolesToUser,
  roleChangesMade,
  setActiveToast,
  auth,
}) {
  const DEFAULT_RESULTS = [];
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(DEFAULT_RESULTS);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState(new Map());
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);

  const isFetching = Object.values(userRoles.userFetches).some((userFetch) => {
    return userFetch.isFetchingUserRoles === true;
  }); 

  useDebounce(
    debounceDelay,
    () => {
      if (query.length) {
        setResultsLoading(true);
        fetchResults(query)
          .then((results) => {
            setResultsLoading(false);
            if (results.length) {
              results = results.filter(
                (user) => user.uid !== userProfile?.userDetails?.id
              );
              setResults(results);
            } else {
              setResults([{ label: "No Results", disabled: true }]);
            }
          })
          .catch((err) => {
            setResultsLoading(false);
            setResults([
              { label: `Error: ${err.message || err}`, disabled: true },
            ]);
          });
      }
    },
    [query]
  );

  const toggleSelect = (user) => {
    if(!user.disabled) {
      setSelectedMap((prev) => {
        const newSelectedMap = new Map(prev);
        if (newSelectedMap.has(user.uid)) {
          newSelectedMap.delete(user.uid);
        } else {
          newSelectedMap.set(user.uid, user);
        }
        return newSelectedMap;
      });
    }
  };

  const handleInputChange = (event) => {
    const inputText = event.target.value;
    setQuery(inputText);

    if (!inputText.length) {
      setResults(DEFAULT_RESULTS);
    }
  };

  const handleDupClose = () => {
    setSelectedMap(new Map());
    setQuery("");
    setResults(DEFAULT_RESULTS);
    setIsCopyDialogOpen(false);
  };

  const handleFetch = () => {
    if (auth.accessToken) {
      const { accessToken } = auth;
      Array.from(selectedMap.entries()).forEach(([uid, userDetails]) => {
        fetchUserRoles({ uid: userDetails.uid, accessToken });
      });

      setIsCopyDialogOpen(false);
      setIsReviewDialogOpen(true);
    }
  };

  const handleReviewClose = () => {
    setSelectedMap(new Map());
    setQuery("");
    setResults(DEFAULT_RESULTS);
    setIsReviewDialogOpen(false);
  };

  const handelReviewCancel = () => {
    setIsCopyDialogOpen(true);
    setIsReviewDialogOpen(false);
  };

  const handleReviewSave = async () => {
    if (!auth.accessToken) return;

    const errorEntries = [];

    const selectedUserRoleMap = new Map();
    Array.from(selectedMap.entries()).forEach(([uid, user]) => {
      const roles = userRoles.userFetches[uid]?.userRolesList || [];
      selectedUserRoleMap.set(uid, {
        user,
        roles: roles,
      });
    });

    const promises = Array.from(selectedUserRoleMap.entries()).map(
      async ([uid, { user, roles }]) => {
        let [diffList, _] = getDifferencesOfRoleLists(
          userRoles.userFetches[userProfile.userDetails.id]?.userRolesList,
          roles
        );

        const addedRoleIds = diffList.map((r) => r.id);
        if (addedRoleIds.length > 0) {
          const postResponse = await assignRolesToUser({
            uid: uid,
            orgId: "*",
            orgName: "",
            roleIds: addedRoleIds,
            accessToken: auth.accessToken,
            reason: `Copied roles from ${userProfile.userDetails.profile.firstName} ${userProfile.userDetails.profile.lastName}`,
          });

          if (postResponse.apierror) {
            errorEntries.push({
              uid,
              user,
              error: postResponse.apierror,
            });
          }
        }
      }
    );

    await Promise.all(promises);

    const errorUids = new Set(errorEntries.map((error) => error.uid));

    const successNames = Array.from(selectedMap.entries())
      .filter(([uid, _]) => !errorUids.has(uid))
      .map(([uid, user]) => `${user.firstName} ${user.lastName}`);

    const errorNames = errorEntries
      .map((error) => selectedMap.get(error.uid))
      .map((user) => `${user.firstName} ${user.lastName}`);

    let toastContent = `Roles copied successfully for ${
      successNames.length === 1
        ? successNames[0]
        : successNames.slice(0, -1).join(", ") +
          " & " +
          successNames[successNames.length - 1]
    }`;

    let toastVariant = TOAST_VARIANTS.POSITIVE;

    if (errorEntries.length == selectedMap.size) {
      toastContent = "Failed to copy roles";
      toastVariant = TOAST_VARIANTS.NEGATIVE;
    } else if (errorEntries.length > 0) {
      toastContent += `, but not for ${errorNames.slice(0, -1).join(", ")} & ${
        errorNames[errorNames.length - 1]
      }`;
      toastVarient = TOAST_VARIANTS.WARNING;
      resetRoleErrors();
    } else {
      roleChangesMade({ uid: userProfile.userDetails.id });
      toastVariant = TOAST_VARIANTS.POSITIVE;
    }

    Array.from(selectedMap.entries())
      .filter(([uid, _]) => !errorUids.has(uid))
      .forEach(([uid, user]) => {
        roleChangesMade({ uid });
      });

    setActiveToast({
      content: toastContent,
      variant: toastVariant,
    });
    handleReviewClose();
  };

  return (
    <>
      <CopyDialog
        isOpen={isCopyDialogOpen}
        onClose={handleDupClose}
        title="Copy Roles"
        dataTestId="copy-dialog"
        id="copy-dialog"
        buttonsProps={[
          {
            id: "copy-dialog-review",
            onClick: () => handleFetch(),
            text: "Review",
            variant: BUTTON_VARIANTS.PRIMARY,
            isDisabled: selectedMap.size === 0 || isFetching,
            isLoading: isFetching,
          },
        ]}
      >
        <div>
          Select user(s) you want to copy{" "}
          {userProfile.userDetails.profile.firstName}{" "}
          {userProfile.userDetails.profile.lastName}'s roles to
        </div>
        <div>
          <SearchBarContainer>
            <SearchInput
              autoComplete="off"
              dataTestId={id}
              icon={
                resultsLoading ? (
                  <div>
                    <IndeterminateProgressCircle size={LOADER_SIZE.SMALL} />
                  </div>
                ) : (
                  <></>
                )
              }
              id={id}
              label="Enter user name or email"
              name={name}
              onChange={handleInputChange}
              placeholder="Search user..."
              value={query}
              isRequired={true}
            />
          </SearchBarContainer>
          {results.length > 0 && (
            <UserListCard>
              {results.map((user, ind) => {
                const isChecked = Boolean(selectedMap.get(user.uid));
                return (
                  <TallListItem
                    onClick={() => toggleSelect(user)}
                    showDivider={true}
                    key={`${id}-${JSON.stringify(user)}`}
                    id={`${id}-list-item-${ind}`}
                  >
                    {!user.disabled && (
                      <ListItemCheckbox
                        checked={isChecked}
                        dataTestId={`${id}-list-checkbox-${ind}`}
                        id={`${id}-list-checkbox-${ind}`}
                        name="user-checkbox"
                      />
                    )}
                    <ListItemText>
                      <ListItemPrimaryText>
                        <UserNameText headingType={HEADING_TYPES.SUBTITLE_3}>
                          {user.label} {user.firstName} {user.lastName}
                        </UserNameText>
                      </ListItemPrimaryText>
                      <ListItemSubtext>{user.email}</ListItemSubtext>
                    </ListItemText>
                  </TallListItem>
                );
              })}
            </UserListCard>
          )}
          <UserChipsContainer>
            {Array.from(selectedMap.values()).map((user, ind) => {
              return (
                <div style={{ marginRight: "0.5rem", marginTop: "0.5rem" }}>
                  <Chip
                    label={user.email}
                    key={`${id}-${JSON.stringify(user)}-chip`}
                    dataTestId={`${id}-list-chip-${ind}`}
                    id={`${id}-list-chip-${ind}`}
                    onRemove={() => toggleSelect(user)}
                    isSelected={true}
                  />
                </div>
              );
            })}
          </UserChipsContainer>
        </div>
      </CopyDialog>

      <ReviewDialog
        isOpen={isReviewDialogOpen}
        onClose={handleReviewClose}
        title="Review Role Copy"
        dataTestId="review-dialog"
        id="review-dialog"
        buttonsProps={[
          {
            id: "review-dialog-cancel",
            onClick: handelReviewCancel,
            text: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            isDisabled: false,
            isLoading: false,
          },
          {
            id: "review-dialog-save",
            onClick: handleReviewSave,
            text: "Confirm",
            variant: BUTTON_VARIANTS.PRIMARY,
            isDisabled: false,
            isLoading: false,
          },
        ]}
      >
        {isFetching ? (
          <div className="loading-container">
            <h4>Loading Users' Roles</h4>
            <IndeterminateProgressCircle />
          </div>
        ) : !isReviewDialogOpen ? (
          <></>
        ) : (
          <ReviewDialogContainer>
            <div>
              <span>
                Are you sure you want to assign{" "}
                {userProfile.userDetails.profile.firstName}{" "}
                {userProfile.userDetails.profile.lastName}'s roles to{" "}
              </span>
              <span style={{ color: "#6B2574", fontWeight: "600" }}>
                {(() => {
                  const names = Array.from(selectedMap.values()).map(
                    (user) => `${user.firstName} ${user.lastName}`
                  );
                  if (names.length === 1) return names[0];
                  return `${names.slice(0, -1).join(", ")} & ${
                    names[names.length - 1]
                  }`;
                })()}
                ?
              </span>
              <span>
                {" "}
                This change will take effect immediately and update their
                access.
              </span>
            </div>
            <SimpleTableWrapper>
              <RolesToolbar>
                <RolesTableTitle>Pull Details</RolesTableTitle>
              </RolesToolbar>
              <SimpleTableContainer>
                <SimpleTable>
                  <SimpleTableBody>
                    {(() => {
                      const selectedUserRoleMap = new Map();
                      Array.from(selectedMap.entries()).forEach(
                        ([uid, user]) => {
                          const roles =
                            userRoles.userFetches[uid]?.userRolesList || [];
                          selectedUserRoleMap.set(uid, {
                            user,
                            roles: roles,
                          });
                        }
                      );

                      return Array.from(selectedUserRoleMap.entries()).map(
                        ([uid, { user, roles }]) => (
                          <SimpleTableRow>
                            <UserTableData>
                              <UserNameText
                                headingType={HEADING_TYPES.SUBTITLE_3}
                              >
                                {user.firstName} {user.lastName}
                              </UserNameText>
                              <ListItemSubtext>{user.email}</ListItemSubtext>
                            </UserTableData>
                            <RoleTableData>
                              <SimpleTable>
                                <SimpleTableBody>
                                  {(() => {
                                    let [diffList, _] =
                                      getDifferencesOfRoleLists(
                                        userRoles.userFetches[
                                          userProfile.userDetails.id
                                        ]?.userRolesList,
                                        roles
                                      );

                                    diffList = userRoles.allRolesList.filter(
                                      (role) =>
                                        diffList.some(
                                          (pulledRole) =>
                                            pulledRole.id === role.id
                                        )
                                    );
                                    return diffList.map((role) => (
                                      <RoleRow role={role} />
                                    ));
                                  })()}
                                </SimpleTableBody>
                              </SimpleTable>
                            </RoleTableData>
                          </SimpleTableRow>
                        )
                      );
                    })()}
                  </SimpleTableBody>
                </SimpleTable>
              </SimpleTableContainer>
            </SimpleTableWrapper>
          </ReviewDialogContainer>
        )}
      </ReviewDialog>
    </>
  );
}

function CopyRolesDialogContainer(props) {
  return props.auth.isAuthenticated ? (
    <CopyRolesDialog {...props} />
  ) : (
    <div></div>
  );
}

export default withAuth(CopyRolesDialogContainer);
